// src/components/Navbar.js
// Apple Dark Mode Style Navigation
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Navbar.css';
import API_URL from '../config';

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const token = localStorage.getItem('token');
  const userPlan = localStorage.getItem('userPlan') || 'free';
  
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [groups, setGroups] = useState([]);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);

  const groupDropdownRef = useRef(null);
  const langDropdownRef = useRef(null);

  // Plan limits
  const GROUP_LIMITS = {
    free: 2,
    plus: 5,
    premium: 20,
    pro: 100
  };

  const languages = [
    { code: 'sv', label: 'SV', name: 'Svenska' },
    { code: 'en', label: 'EN', name: 'English' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  // Fetch groups
  const fetchGroups = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/groups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('token');
          setIsLoggedIn(false);
        }
        setGroups([]);
        return;
      }
      
      const data = await response.json();
      setGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setIsLoggedIn(!!token);
    if (token) fetchGroups();
  }, [token]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleGroupsUpdate = () => fetchGroups();
    window.addEventListener('groupsUpdated', handleGroupsUpdate);
    return () => window.removeEventListener('groupsUpdated', handleGroupsUpdate);
  }, [token]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (groupDropdownRef.current && !groupDropdownRef.current.contains(event.target)) {
        setShowGroupDropdown(false);
      }
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setShowLangDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const handleGroupSelect = (group) => {
    localStorage.setItem('selectedGroupId', group._id);
    setShowGroupDropdown(false);
    setMobileMenuOpen(false);
    navigate(`/groups/${group._id}`);
  };

  const handleCreateNewGroup = async () => {
    const currentLimit = GROUP_LIMITS[userPlan];
    
    if (currentLimit && groups.length >= currentLimit) {
      alert(t('nav.alerts.maxGroupsReached'));
      setShowGroupDropdown(false);
      setMobileMenuOpen(false);
      navigate('/priser');
      return;
    }

    setCreatingGroup(true);
    setShowGroupDropdown(false);
    setMobileMenuOpen(false);

    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      const response = await fetch(`${API_URL}/api/groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          groupName: t('nav.newGroupDefault'),
          meetingFrequency: { dayOfWeek: '', time: '' },
          threshold: null,
          timezone: userTimezone,
          location: '',
          invitationAdvance: 7,
          reminderAdvance: 3,
          decisionTime: '18:00'
        })
      });

      if (response.ok) {
        const newGroup = await response.json();
        window.dispatchEvent(new Event('groupsUpdated'));
        localStorage.setItem('selectedGroupId', newGroup._id);
        navigate(`/groups/${newGroup._id}`);
      } else {
        const error = await response.json();
        alert(t('nav.alerts.createGroupError') + error.message);
      }
    } catch (error) {
      console.error('Error creating group:', error);
      alert(t('nav.alerts.createGroupFailed'));
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    setMobileMenuOpen(false);
    setIsLoggedIn(false);
    navigate('/login');
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setShowLangDropdown(false);
    setMobileMenuOpen(false);
  };

  const hasReachedLimit = () => {
    const limit = GROUP_LIMITS[userPlan];
    return limit && groups.length >= limit;
  };

  const isActivePath = (path) => location.pathname === path;
  const isGroupPath = () => location.pathname.startsWith('/groups/');

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          {/* Logo */}
          <Link to="/" className="navbar-logo">
            <div className="navbar-logo-icon">M</div>
            <span>{t('nav.logo')}</span>
          </Link>

          {/* Desktop Navigation Links */}
          <div className="navbar-links">
            {/* Public links */}
            <Link 
              to="/hur" 
              className={`navbar-link ${isActivePath('/hur') ? 'active' : ''}`}
            >
              {t('nav.howItWorks')}
            </Link>
            <Link 
              to="/priser" 
              className={`navbar-link ${isActivePath('/priser') ? 'active' : ''}`}
            >
              {t('nav.pricing')}
            </Link>
            <Link 
              to="/kontakt" 
              className={`navbar-link ${isActivePath('/kontakt') ? 'active' : ''}`}
            >
              {t('nav.contact')}
            </Link>
            
            {/* Logged in links - Dashboard & Mina Grupper */}
            {isLoggedIn && (
              <>
                <div className="navbar-divider" />
                
                <Link 
                  to="/dashboard" 
                  className={`navbar-link ${isActivePath('/dashboard') ? 'active' : ''}`}
                >
                  {t('nav.dashboard')}
                </Link>

                {/* Mina Grupper Dropdown */}
                <div className="navbar-dropdown" ref={groupDropdownRef}>
                  <button 
                    className={`navbar-dropdown-toggle ${showGroupDropdown || isGroupPath() ? 'active' : ''}`}
                    onClick={() => setShowGroupDropdown(!showGroupDropdown)}
                  >
                    {t('nav.myGroups')}
                    <svg className="navbar-dropdown-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9l6 6 6-6"/>
                    </svg>
                  </button>
                  
                  {showGroupDropdown && (
                    <div className="navbar-dropdown-menu">
                      {groups.length === 0 ? (
                        <div className="navbar-dropdown-item disabled">
                          {t('nav.noGroups')}
                        </div>
                      ) : (
                        groups.map(group => (
                          <div
                            key={group._id}
                            className={`navbar-dropdown-item ${location.pathname === `/groups/${group._id}` ? 'active' : ''}`}
                            onClick={() => handleGroupSelect(group)}
                          >
                            {group.groupName}
                            {group.status === 'draft' && (
                              <span className="navbar-draft-badge">{t('nav.draftBadge')}</span>
                            )}
                          </div>
                        ))
                      )}
                      
                      <div className="navbar-dropdown-divider" />
                      
                      <div
                        className={`navbar-dropdown-item create-new ${creatingGroup ? 'disabled' : ''}`}
                        onClick={creatingGroup ? null : handleCreateNewGroup}
                      >
                        {creatingGroup ? t('nav.creatingGroup') : `+ ${t('nav.createNewGroup')}`}
                      </div>
                      
                      {hasReachedLimit() && (
                        <>
                          <div className="navbar-dropdown-divider" />
                          <div
                            className="navbar-dropdown-item upgrade"
                            onClick={() => {
                              setShowGroupDropdown(false);
                              navigate('/priser');
                            }}
                          >
                            {t('nav.upgradeForMore')}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Desktop Actions (Auth + Language) */}
          <div className="navbar-actions">
            {/* Auth Buttons */}
            {isLoggedIn ? (
              <button onClick={handleLogout} className="btn btn-ghost">
                {t('nav.logout')}
              </button>
            ) : (
              <>
                <Link to="/login" className="btn btn-ghost">
                  {t('nav.login')}
                </Link>
                <Link to="/signup" className="btn btn-primary">
                  {t('home.hero.ctaButton')}
                </Link>
              </>
            )}

            {/* Language Selector */}
            <div className="navbar-dropdown" ref={langDropdownRef}>
              <button 
                className="navbar-lang-toggle"
                onClick={() => setShowLangDropdown(!showLangDropdown)}
              >
                🌐 {currentLanguage.label}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 9l6 6 6-6"/>
                </svg>
              </button>
              
              {showLangDropdown && (
                <div className="navbar-lang-menu">
                  {languages.map(lang => (
                    <div
                      key={lang.code}
                      className={`navbar-dropdown-item ${i18n.language === lang.code ? 'active' : ''}`}
                      onClick={() => changeLanguage(lang.code)}
                    >
                      <strong>{lang.label}</strong> – {lang.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Hamburger */}
          <button 
            className={`navbar-hamburger ${mobileMenuOpen ? 'active' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`navbar-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="navbar-mobile-links">
          {/* Public Links */}
          <Link to="/" className="navbar-mobile-link">{t('nav.home')}</Link>
          <Link to="/hur" className="navbar-mobile-link">{t('nav.howItWorks')}</Link>
          <Link to="/priser" className="navbar-mobile-link">{t('nav.pricing')}</Link>
          <Link to="/kontakt" className="navbar-mobile-link">{t('nav.contact')}</Link>
          
          {/* Logged In Section */}
          {isLoggedIn && (
            <>
              <div className="navbar-mobile-divider" />
              <div className="navbar-mobile-section-title">
                {t('nav.myAccount')}
              </div>
              
              <Link to="/dashboard" className="navbar-mobile-link">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7"/>
                  <rect x="14" y="3" width="7" height="7"/>
                  <rect x="14" y="14" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/>
                </svg>
                {t('nav.dashboard')}
              </Link>
              
              <div className="navbar-mobile-divider" />
              <div className="navbar-mobile-section-title">
                {t('nav.myGroups')}
              </div>
              
              {groups.length === 0 ? (
                <div className="navbar-mobile-empty">
                  {t('nav.noGroups')}
                </div>
              ) : (
                groups.map(group => (
                  <div
                    key={group._id}
                    className="navbar-mobile-link navbar-mobile-group"
                    onClick={() => handleGroupSelect(group)}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    {group.groupName}
                    {group.status === 'draft' && (
                      <span className="navbar-draft-badge">{t('nav.draftBadge')}</span>
                    )}
                  </div>
                ))
              )}
              
              {/* Create New Group Button */}
              <button 
                className="navbar-mobile-create-btn"
                onClick={handleCreateNewGroup}
                disabled={creatingGroup}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                {creatingGroup ? t('nav.creatingGroup') : t('nav.createNewGroup')}
              </button>
            </>
          )}
        </div>

        <div className="navbar-mobile-divider" />

        {/* Language Selection */}
        <div className="navbar-mobile-lang">
          {languages.map(lang => (
            <button
              key={lang.code}
              className={`navbar-mobile-lang-btn ${i18n.language === lang.code ? 'active' : ''}`}
              onClick={() => changeLanguage(lang.code)}
            >
              {lang.label}
            </button>
          ))}
        </div>

        {/* Auth Actions */}
        <div className="navbar-mobile-actions">
          {isLoggedIn ? (
            <button onClick={handleLogout} className="btn btn-secondary btn-full">
              {t('nav.logout')}
            </button>
          ) : (
            <>
              <Link to="/signup" className="btn btn-primary btn-full">
                {t('home.hero.ctaButton')}
              </Link>
              <Link to="/login" className="btn btn-secondary btn-full">
                {t('nav.login')}
              </Link>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default Navbar;
